import React, { useEffect, useState } from "react";
import { useMutation, useQuery } from "@apollo/client";
import { useNavigate } from "react-router-dom";
import {
  Alert,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  useMediaQuery,
} from "@mui/material";
import { toggleSnackbar } from "../../ReactiveVariables";
import {
  CANCEL_GROUP_ORDER,
  CREATE_GROUP_ORDER,
  MY_GROUP_ORDERS,
  REOPEN_GROUP_ORDER,
  UPDATE_GROUP_ORDER_SETTINGS,
} from "../../Graphql/group-order.gql";
import {
  budgetLabel,
  formatDelivery,
  repStatusLabel,
  serverBudget,
  shareUrl,
} from "../../services/group-order/group-order.utils";
import { GroupOrderForm } from "./group-order-form";
import "./group-order.css";

const money = new Intl.NumberFormat("en-US", { style: "currency", currency: "USD" });
const isActive = (go: any) => ["Open", "Ready", "CheckingOut"].includes(go.status);
const notify = (message: string, variant = "success") =>
  toggleSnackbar({ status: true, message, variant });
const calmFailure = () => notify("That didn't go through. Please try again in a moment.", "error");

export const MyGroupOrders = () => {
  const navigate = useNavigate();
  const { data, loading, error, refetch } = useQuery(MY_GROUP_ORDERS, {
    fetchPolicy: "network-only",
  });
  const [createGroupOrder] = useMutation(CREATE_GROUP_ORDER);
  const [updateSettings] = useMutation(UPDATE_GROUP_ORDER_SETTINGS);
  const [cancelGroupOrder] = useMutation(CANCEL_GROUP_ORDER);
  const [reopenGroupOrder] = useMutation(REOPEN_GROUP_ORDER);
  const [reopeningId, setReopeningId] = useState<string | null>(null);
  const [editing, setEditing] = useState<any>(null); // null | "new" | groupOrder
  const [confirmCancel, setConfirmCancel] = useState<any>(null);
  const [manualLink, setManualLink] = useState<string | null>(null);
  const isDesktop = useMediaQuery((theme: any) => theme.breakpoints.up("md"));

  useEffect(() => {
    const onFocus = () => refetch().catch(() => {});
    window.addEventListener("focus", onFocus);
    return () => window.removeEventListener("focus", onFocus);
  }, [refetch]);

  // Native share sheet when available, otherwise the clipboard. If both fail, show the link
  // so the rep can copy it by hand.
  const shareLink = async (go: any, successMessage = "Link copied. Text it to the office.") => {
    const link = shareUrl(window.location.origin, go.shareToken);
    if (navigator.share) {
      try {
        await navigator.share({
          title: go.label || "Admin order",
          text: "Pick lunch for the office here:",
          url: link,
        });
        return;
      } catch (e: any) {
        if (e?.name === "AbortError") return;
      }
    }
    try {
      await navigator.clipboard.writeText(link);
      notify(successMessage);
    } catch (e) {
      setManualLink(link);
    }
  };

  const onSubmit = async (input: any) => {
    let created: any = null;
    try {
      if (editing === "new") {
        const res = await createGroupOrder({ variables: { input } });
        created = res.data.createGroupOrder;
      } else {
        await updateSettings({ variables: { id: editing._id, input } });
      }
    } catch (e) {
      calmFailure();
      return;
    }
    // The order is saved at this point; a refetch problem must not read as a failed save.
    setEditing(null);
    refetch().catch(() => {});
    if (created) {
      // iOS Safari drops the user gesture across the awaited mutation, so show the link dialog.
      setManualLink(shareUrl(window.location.origin, created.shareToken));
      notify("Admin order created.");
    } else {
      notify("Details saved.");
    }
  };

  // Releases a CheckingOut group order (e.g. the rep left checkout) so the office can edit again.
  // The server returns it Placed instead if its order was already paid.
  const onLetOfficeEdit = async (go: any) => {
    if (reopeningId) return;
    setReopeningId(go._id);
    try {
      const res = await reopenGroupOrder({ variables: { id: go._id } });
      const status = res.data?.reopenGroupOrder?.status;
      if (status === "Placed") notify("This order has already been placed.", "info");
      else if (status === "Open" || status === "Ready")
        notify("Checkout released. The office can see the cart again.");
      else calmFailure();
      refetch().catch(() => {});
    } catch (e) {
      calmFailure();
    } finally {
      setReopeningId(null);
    }
  };

  const onCancelConfirmed = async () => {
    const go = confirmCancel;
    setConfirmCancel(null);
    try {
      await cancelGroupOrder({ variables: { id: go._id } });
      await refetch();
      notify("Admin order cancelled.");
    } catch (e) {
      calmFailure();
    }
  };

  const rows = [...(data?.myGroupOrders || [])].sort(
    (a: any, b: any) => (a.status === "Ready" ? -1 : 0) - (b.status === "Ready" ? -1 : 0),
  );

  const RowActions = ({ go }: any) => {
    const name = go.label || "this admin order";
    if (!isActive(go)) {
      return go.status === "Placed" && go.orderId ? (
        <Button
          size="small"
          variant="outlined"
          className="group-action-btn"
          onClick={() => navigate(`/orders/${go.orderId}`)}
          aria-label={`View order for ${name}`}>
          View order
        </Button>
      ) : null;
    }
    return (
      <>
        {go.status === "Ready" && (
          <Button
            size="small"
            variant="contained"
            className="group-action-btn"
            onClick={() => navigate(`/group-orders/${go._id}/caterers`)}
            aria-label={`Review and check out ${name}`}>
            Review & check out
          </Button>
        )}
        <Button
          size="small"
          variant="outlined"
          className="group-action-btn"
          onClick={() => shareLink(go)}
          aria-label={`Share link for ${name}`}>
          Share link
        </Button>
        {go.status !== "Ready" && (
          <Button
            size="small"
            variant="outlined"
            className="group-action-btn"
            onClick={() => navigate(`/group-orders/${go._id}/caterers`)}
            aria-label={`Open cart for ${name}`}>
            Open cart
          </Button>
        )}
        {go.status === "CheckingOut" && (
          <Button
            size="small"
            variant="outlined"
            className="group-action-btn"
            disabled={!!reopeningId}
            onClick={() => onLetOfficeEdit(go)}
            aria-label={`Release checkout for ${name}`}>
            Release checkout
          </Button>
        )}
        <Button
          size="small"
          variant="outlined"
          className="group-action-btn"
          onClick={() => setEditing(go)}
          aria-label={`Edit settings for ${name}`}>
          Edit settings
        </Button>
        <Button
          size="small"
          variant="outlined"
          className="group-action-btn"
          color="error"
          onClick={() => setConfirmCancel(go)}
          aria-label={`Cancel ${name}`}>
          Cancel
        </Button>
      </>
    );
  };

  const BudgetCell = ({ go }: any) => {
    const budget = serverBudget(go);
    return (
      <span className={budget.isOver ? "group-budget--over" : ""}>
        {budgetLabel(budget, (n: number) => money.format(n))}
        {budget.isOver && <span className="group-order-marker">Over budget</span>}
      </span>
    );
  };

  return (
    <div className="group-orders-page">
      <div className="group-orders-intro">
        <div>
          <Chip label="New feature" color="primary" size="small" />
          <h2>No time to order for an upcoming client meeting?</h2>
          <p>
            Send your client a link and let them create the order themselves. You set the budget,
            get a text when it's ready, then review and pay. Perfect for sales reps and client
            visits.
          </p>
        </div>
        <Button variant="contained" onClick={() => setEditing("new")}>
          Create a link
        </Button>
      </div>
      {loading && !data ? (
        <div style={{ padding: 32, textAlign: "center" }}>
          <CircularProgress aria-label="Loading admin orders" />
        </div>
      ) : error && !data ? (
        <Alert severity="error" action={<Button onClick={() => refetch()}>Retry</Button>}>
          We couldn't load your admin orders.
        </Alert>
      ) : !rows.length ? (
        <p>Nothing here yet. Create a link and send it to your client.</p>
      ) : isDesktop ? (
        <TableContainer>
          <Table aria-label="Admin orders">
            <TableHead>
              <TableRow>
                <TableCell>Name</TableCell>
                <TableCell>Delivery</TableCell>
                <TableCell>Caterer</TableCell>
                <TableCell>Budget</TableCell>
                <TableCell>Status</TableCell>
                <TableCell />
              </TableRow>
            </TableHead>
            <TableBody>
              {rows.map((go: any) => (
                <TableRow key={go._id} className={go.status === "Ready" ? "group-row--ready" : ""}>
                  <TableCell>{go.label}</TableCell>
                  <TableCell>
                    {go.street ? `${go.street} · ` : ""}
                    {formatDelivery(go)}
                  </TableCell>
                  <TableCell>{go.catererName || "Not picked yet"}</TableCell>
                  <TableCell>
                    <BudgetCell go={go} />
                  </TableCell>
                  <TableCell>{repStatusLabel(go)}</TableCell>
                  <TableCell>
                    <RowActions go={go} />
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      ) : (
        <div className="group-cards">
          {rows.map((go: any) => (
            <div
              key={go._id}
              className={`group-card ${go.status === "Ready" ? "group-row--ready" : ""}`}>
              <div className="group-card__head">
                <strong>{go.label}</strong>
                <span className="group-card__status">{repStatusLabel(go)}</span>
              </div>
              <div>
                {go.catererName || "Caterer not picked yet"} · {formatDelivery(go)}
              </div>
              <div>
                <BudgetCell go={go} />
              </div>
              <div className="group-card__actions">
                <RowActions go={go} />
              </div>
            </div>
          ))}
        </div>
      )}
      {editing && (
        <GroupOrderForm
          initial={editing === "new" ? null : editing}
          onSubmit={onSubmit}
          onCancel={() => setEditing(null)}
        />
      )}
      <Dialog open={!!confirmCancel} onClose={() => setConfirmCancel(null)}>
        <DialogTitle>Cancel this admin order?</DialogTitle>
        <DialogContent>
          <DialogContentText>
            The office link will stop working and the cart can't be edited.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmCancel(null)}>Keep it</Button>
          <Button color="error" onClick={onCancelConfirmed}>
            Cancel admin order
          </Button>
        </DialogActions>
      </Dialog>
      <Dialog open={!!manualLink} onClose={() => setManualLink(null)} fullWidth maxWidth="sm">
        <DialogTitle>Send this link to the office</DialogTitle>
        <DialogContent>
          <DialogContentText>Share the link below, or select it and copy it.</DialogContentText>
          <TextField
            fullWidth
            margin="dense"
            value={manualLink || ""}
            InputProps={{ readOnly: true }}
            onFocus={(e) => e.target.select()}
            autoFocus
          />
        </DialogContent>
        <DialogActions>
          <Button
            variant="contained"
            onClick={() => shareLink({ shareToken: (manualLink || "").split("/").pop() })}>
            Share
          </Button>
          <Button onClick={() => setManualLink(null)}>Done</Button>
        </DialogActions>
      </Dialog>
    </div>
  );
};
